import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import RoundPickerTrigger from './round_picker_trigger';
import useOutsideClick from '../hooks/useOutsideClick';
import { themedTextClass } from '../utils/css_utils';
import { centerItemInList } from '../utils/scroll_utils'
import { roundsFailedMessage } from '../utils/round_label_utils';

const RoundSelector = ({
  rounds, roundDates, selectedRoundId, onRoundChange, onOpen, formatLabel, showPlayAt = false, isManagerSite = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Round-list refresh state, local like the rounds modal's preview state:
  // set around the awaited onOpen, never in the store.
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null)
  const selectedRef = useRef(null)

  // Each open owns the refresh it started: a result landing after the list
  // was closed and reopened (or the selector unmounted) is ignored.
  const openGenerationRef = useRef(0)
  useEffect(() => () => { openGenerationRef.current += 1 }, [])

  // The list is capped at three rounds; open with the selected one centred.
  // Re-run once the refreshed rounds replace the spinner (isLoading flips).
  useLayoutEffect(() => {
    if (isOpen && !isLoading) {
      centerItemInList(listRef.current, selectedRef.current)
    }
  }, [ isOpen, isLoading ])

  // Close the open dropdown on any click/tap outside the selector; only
  // listens while open.
  const closeList = useCallback(() => setIsOpen(false), []);
  useOutsideClick(containerRef, closeList, isOpen);

  const selectedRound = rounds.find((round) => String(round.id) === String(selectedRoundId));
  if (!selectedRound) return null;

  const handleChange = (newRoundId) => {
    setIsOpen(false);
    onRoundChange(newRoundId);
  };

  // Opening asks the owner to refresh (the page-level selector refetches the
  // round list, like the mobile picker does on open). While that runs the
  // list shows a spinner rather than a possibly stale set of rounds, and its
  // error message when it fails - same treatment as the modal's previews.
  const handleTriggerClick = async () => {
    const generation = ++openGenerationRef.current;

    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    if (!onOpen) return;

    setError(false);
    setIsLoading(true);
    const result = await onOpen();

    if (generation !== openGenerationRef.current) return;
    setIsLoading(false);
    setError(result?.status === 'error');
  };

  const renderRounds = () => {
    if (isLoading) {
      return (
        <div className="lbv2__loader">
          <Loader type="Oval" color={ '#333F48' } height={ 20 } width={ 12 }></Loader>
        </div>
      );
    }

    if (error) {
      return <div className="lbv2__error">{roundsFailedMessage()}</div>;
    }

    return rounds.map((round) => (
      <button
        key={round.id}
        ref={String(round.id) === String(selectedRoundId) ? selectedRef : null}
        className={`lbv2__round-btn ${String(round.id) === String(selectedRoundId) ? themedTextClass(isManagerSite) : ''}`}
        onClick={() => handleChange(String(round.id))}
      >
        <div className="lbv2__round-btn-text">
          <div className="lbv2__round-btn-text-name">{formatLabel(round)}</div>
          {roundDates?.[round.id] && (
            <div className="lbv2__round-btn-text-date">({roundDates[round.id]})</div>
          )}
        </div>
      </button>
    ));
  };

  return (
    <div className="lbv2__round-selector" ref={containerRef}>
      <RoundPickerTrigger
        label={formatLabel(selectedRound)}
        date={roundDates?.[selectedRound.id]}
        isOpen={isOpen}
        showPlayAt={showPlayAt}
        onClick={handleTriggerClick}
      />
      {isOpen && (
        <div className="lbv2__rounds-list" ref={listRef}>
          {renderRounds()}
        </div>
      )}
    </div>
  );
};

RoundSelector.propTypes = {
  rounds: PropTypes.array.isRequired,
  roundDates: PropTypes.object,
  selectedRoundId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRoundChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  formatLabel: PropTypes.func.isRequired,
  showPlayAt: PropTypes.bool,
  isManagerSite: PropTypes.bool,
};

export default RoundSelector;
